import { useState } from 'react';
import { Avatar, Dots, GhostButton, Screen } from '../components/Chrome.js';
import type { Profile } from '../lib/useGame.js';
import { inviteUrl } from '../lib/useRouter.js';
import { Icon } from '../components/Icon.js';
import { color } from '../theme.js';

/** Friend room. Unranked by design — see plan doc §8.6. */
export function RoomScreen({
  code,
  profile,
  onCancel,
}: {
  code: string;
  profile: Profile | null;
  onCancel: () => void;
}) {
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);

  const flash = (what: 'code' | 'link') => {
    setCopied(what);
    window.setTimeout(() => setCopied(null), 1400);
  };

  const copyCode = () => {
    void navigator.clipboard.writeText(code).then(() => flash('code'));
  };
  const copyLink = () => {
    void navigator.clipboard.writeText(inviteUrl(code)).then(() => flash('link'));
  };

  return (
    <Screen brackets="diagonal">
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div style={{ width: 'min(92cqw,480px)', display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 11, letterSpacing: '.34em', fontWeight: 600, color: 'rgba(166,255,46,.65)' }}>FRIEND MATCH · UNRANKED</span>
            <span style={{ fontWeight: 700, fontSize: 'clamp(22px,5cqmin,38px)', letterSpacing: '.02em' }}>ROOM CREATED</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ textAlign: 'center', fontSize: 11, letterSpacing: '.24em', fontWeight: 600, color: 'rgba(234,255,234,.4)' }}>SHARE THIS CODE</span>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 22,
                border: '1.5px solid rgba(166,255,46,.4)',
                borderRadius: 15,
                background: 'rgba(166,255,46,.06)',
                animation: 'rmCopy 1.8s ease-in-out infinite',
              }}
            >
              <span
                style={{
                  fontFamily: "'JetBrains Mono',monospace",
                  fontWeight: 800,
                  fontSize: 'clamp(30px,7cqmin,54px)',
                  lineHeight: 1,
                  letterSpacing: '.14em',
                  color: color.lime,
                  textShadow: '0 0 16px rgba(166,255,46,.5)',
                }}
              >
                {code}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <GhostButton tone="lime" onClick={copyCode} style={{ flex: 1, padding: 14, gap: 8 }}>
                <Icon name={copied === 'code' ? 'check' : 'copy'} size={17} />
                {copied === 'code' ? 'COPIED' : 'COPY CODE'}
              </GhostButton>
              <GhostButton onClick={copyLink} style={{ flex: 1, padding: 14, gap: 8 }}>
                <Icon name={copied === 'link' ? 'check' : 'share'} size={17} />
                {copied === 'link' ? 'COPIED' : 'SHARE LINK'}
              </GhostButton>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 26,
              padding: 18,
              border: '1px solid rgba(234,255,234,.1)',
              borderRadius: 14,
              background: 'rgba(255,255,255,.02)',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}>
              <Avatar name={profile?.displayName ?? 'You'} side="you" size={58} />
              <span style={{ fontWeight: 700, fontSize: 14, letterSpacing: '.04em' }}>YOU</span>
              <span style={{ fontSize: 10, letterSpacing: '.1em', color: color.lime, fontWeight: 600 }}>HOST · READY</span>
            </div>

            <span style={{ fontWeight: 800, fontSize: 22, color: 'rgba(234,255,234,.3)' }}>VS</span>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}>
              <div
                style={{
                  width: 58,
                  height: 58,
                  borderRadius: 12,
                  border: '1.5px dashed rgba(234,255,234,.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,.02)',
                  animation: 'rmWait 1.6s ease-in-out infinite',
                  fontSize: 26,
                  color: 'rgba(234,255,234,.3)',
                }}
              >
                ?
              </div>
              <span style={{ fontWeight: 700, fontSize: 14, letterSpacing: '.04em', color: 'rgba(234,255,234,.5)' }}>OPPONENT</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, letterSpacing: '.08em', color: 'rgba(234,255,234,.4)', fontWeight: 600 }}>
                WAITING <Dots tint="rgba(234,255,234,.5)" />
              </span>
            </div>
          </div>

          <GhostButton tone="danger" onClick={onCancel} style={{ alignSelf: 'center', padding: '13px 34px' }}>
            CANCEL ROOM
          </GhostButton>
        </div>
      </div>
    </Screen>
  );
}
